/** @jsxImportSource @emotion/react */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SubTitleGroup, SubTitleLayout } from 'components/layouts/ContentLayout';
import { IpCheckResult, IpCheckResultCondition } from 'models/ip/IpCheckResult';
import { findCheckResultParaPoint } from 'apis/ip/IpCheckResult';
import CustomDialog from 'components/modals/common/CustomDialog';
import CustomGrid from 'components/grids/CustomGrid';

type CheckResultParaPointModalProps = {
  close: () => void;
  condition: IpCheckResultCondition;
};

const CheckResultParaPointModal = ({ condition, close }: CheckResultParaPointModalProps) => {
  const gridRef = useRef<any>();
  const { t } = useTranslation();
  const [rowData, setRowData] = useState<IpCheckResult[]>([]);

  useEffect(() => {
    handleSearch(condition);
  }, []);

  const handleSearch = async (condition: IpCheckResultCondition) => {
    const response = await findCheckResultParaPoint(condition);
    if (response) {
      const rowData = response || [];
      setRowData(rowData);
    }
  };

  const layoutDefinition = [
    {
      binding: 'paraName',
      header: String(t('ip.grid.파라미터명', '파라미터명')),
      width: 100,
    },
    {
      header: String(t('ip.grid.포인터 번호', '포인터 번호')),
      columns: [
        {
          binding: 'paraPointNo',
          header: ' ',
          align: 'center',
          width: 77,
        },
      ],
    },
    {
      binding: 'paraPointName',
      header: String(t('ip.grid.측정개소명', '측정개소명')),
      width: 135,
    },
    {
      binding: 'paraPointDesc',
      header: String(t('ip.grid.측정개소 설명', '측정개소 설명')),
      width: 130,
    },
    {
      header: String(t('ip.grid.하한', '하한')),
      columns: [
        {
          binding: 'lowerBound',
          header: ' ',
          width: 67,
        },
      ],
    },
    {
      header: String(t('ip.grid.상한', '상한')),
      columns: [
        {
          binding: 'upperBound',
          header: ' ',
          width: 67,
        },
      ],
    },
    {
      header: String(t('ip.grid.FAT Maker', 'FAT Maker')),
      columns: [
        {
          binding: 'fatMakerTargetFlag',
          align: 'center',
          header: String(t('ip.grid.대상여부', '대상여부')),
          width: 74,
        },
        {
          binding: 'fatMakerValue',
          header: String(t('ip.grid.Value', 'Value')),
          width: 80,
        },
        {
          binding: 'fatMakerResult',
          header: String(t('ip.grid.Result', 'Result')),
          width: 80,
          align: 'center',
        },
        {
          binding: 'fatMakerComment',
          header: String(t('ip.grid.Comment', 'Comment')),
          width: 80,
        },
      ],
    },
    {
      header: String(t('ip.grid.FAT 엔솔', 'FAT 엔솔')),
      columns: [
        {
          binding: 'fatEnsolTargetFlag',
          header: String(t('ip.grid.대상여부', '대상여부')),
          align: 'center',
          width: 74,
        },
        {
          binding: 'fatEnsolValue',
          header: String(t('ip.grid.Value', 'Value')),
          width: 80,
        },
        {
          binding: 'fatEnsolComment',
          header: String(t('ip.grid.Result', 'Result')),
          width: 80,
          align: 'center',
        },
        {
          binding: 'fatEnsolComment',
          header: String(t('ip.grid.Comment', 'Comment')),
          width: 80,
        },
      ],
    },
    {
      header: String(t('ip.grid.SAT Maker', 'SAT Maker')),
      columns: [
        {
          binding: 'satMakerTargetFlag',
          header: String(t('ip.grid.대상여부', '대상여부')),
          align: 'center',
          width: 74,
        },
        {
          binding: 'satMakerValue',
          header: String(t('ip.grid.Value', 'Value')),
          width: 80,
        },
        {
          binding: 'satMakerResult',
          header: String(t('ip.grid.Result', 'Result')),
          width: 80,
          align: 'center',
        },
        {
          binding: 'satMakerComment',
          header: String(t('ip.grid.Comment', 'Comment')),
          width: 80,
        },
      ],
    },
    {
      header: String(t('ip.grid.SAT 엔솔', 'SAT 엔솔')),
      columns: [
        {
          binding: 'satEnsolTargetFlag',
          header: String(t('ip.grid.대상여부', '대상여부')),
          align: 'center',
          width: 74,
        },
        {
          binding: 'satEnsolValue',
          header: String(t('ip.grid.Value', 'Value')),
          width: 80,
        },
        {
          binding: 'satEnsolResult',
          header: String(t('ip.grid.Result', 'Result')),
          width: 80,
          align: 'center',
        },
        {
          binding: 'satEnsolComment',
          header: String(t('ip.grid.Comment', 'Comment')),
          width: 80,
        },
      ],
    },
  ];

  const excludePin = useMemo(() => {
    const convertFlat = (acc, cur) => {
      if ('columns' in cur && Array.isArray(cur?.columns)) {
        return cur.columns.reduce(convertFlat, acc);
      }
      return (cur?.binding || '').startsWith('sat') || (cur?.binding || '').startsWith('fat')
        ? [...acc, cur?.binding]
        : acc;
    };

    return (layoutDefinition || []).reduce(convertFlat, []);
  }, [layoutDefinition]);

  const onInitialized = (grid) => {
    gridRef.current = grid;
  };

  return (
    <CustomDialog
      open={true}
      onClose={close}
      title={t('ip.label.IP 점검 측정개소 결과 조회', 'IP 점검 측정개소 결과 조회')}
    >
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('ip.label.측정개소 결과 조회', '측정개소 결과 조회')}</h3>
          <span className="total">
            {t('com.label.총', '총')} <span>{(rowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        height={380}
        isSelector={false}
        isReadOnly={true}
        excludePin={excludePin}
        excludeFilter={['paraName']}
        initialized={onInitialized}
      />
    </CustomDialog>
  );
};
export default CheckResultParaPointModal;
